import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Camera, Upload, CheckCircle, ArrowRight, X, FileImage, AlertCircle } from 'lucide-react';
import CorporateHeader from './CorporateHeader';

const PACKAGE_NAMES = {
  starter_photoshoot: 'The Starter Look',
  brand_builder_photoshoot: 'The Brand Builder',
  ceo_experience_photoshoot: 'The CEO Experience',
};

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AIPhotoshootsIntakePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id') || '';
  const packageId = searchParams.get('package') || '';
  const packageName = PACKAGE_NAMES[packageId] || 'AI Luxury Photoshoot';

  const [form, setForm] = useState({
    fullName: '', email: '',
    desiredSetting: '', wardrobePreferences: '', colorPreferences: '', additionalNotes: '',
    photoUsage: '', photoUsageOther: '',
  });
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    const valid = files.filter(f => ['image/jpeg', 'image/png', 'image/jpg'].includes(f.type) && f.size <= 10 * 1024 * 1024);
    if (valid.length !== files.length) setError('Some files were rejected. Only JPG/PNG under 10MB accepted.');
    setPhotos(prev => [...prev, ...valid].slice(0, 2));
  };

  const removePhoto = (idx) => setPhotos(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.fullName || !form.email) { setError('Name and email are required.'); return; }
    if (photos.length === 0) { setError('Please upload at least one photo.'); return; }
    if (!form.photoUsage) { setError('Please select what you will be using these photos for.'); return; }
    if (form.photoUsage === 'Other' && !form.photoUsageOther.trim()) { setError('Please specify how you will use these photos.'); return; }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('fullName', form.fullName);
      formData.append('email', form.email);
      formData.append('phone', '');
      formData.append('packageId', packageId || 'unknown');
      formData.append('sessionId', sessionId);
      formData.append('styleSelections', '');
      formData.append('preferredScene', form.desiredSetting);
      formData.append('outfitStyle', form.wardrobePreferences);
      formData.append('vibeDirection', form.colorPreferences);
      formData.append('enhancements', '[]');
      formData.append('intendedUsage', '[]');
      formData.append('additionalNotes', form.additionalNotes);
      const usageValue = form.photoUsage === 'Other' ? `Other: ${form.photoUsageOther}` : form.photoUsage;
      formData.append('photoUsage', usageValue);
      photos.forEach(p => formData.append('photos', p));

      const res = await fetch(`${API_URL}/api/ai-photoshoot/intake`, { method: 'POST', body: formData });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Submission failed'); }
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // POST-SUBMISSION CONFIRMATION
  if (submitted) {
    return (
      <div className="min-h-screen bg-black">
        <CorporateHeader />
        <div className="pt-28 pb-20">
          <div className="container mx-auto px-4 max-w-2xl text-center">
            <div className="bg-gray-900/50 border border-amber-800/30 rounded-2xl p-8 sm:p-12">
              <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-6" />
              <h1 className="text-3xl font-bold text-white mb-4" data-testid="submission-confirmed">Your photos and details have been successfully received.</h1>
              <p className="text-amber-400 font-semibold text-lg mb-6">Your photoshoot is now in production. You will receive your preview images within 24-48 hours.</p>

              <Button onClick={() => navigate('/')} className="bg-gradient-to-r from-amber-600 to-yellow-600 text-black font-bold px-8 py-3 rounded-full">
                Return Home
              </Button>
            </div>

            <div className="mt-8 text-center">
              <p className="text-amber-400/80 text-sm font-semibold tracking-wider">eBlackBook AI Luxury Photoshoots</p>
              <p className="text-gray-600 text-xs mt-1">Powered by eBlackBookListings</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <CorporateHeader />
      <div className="pt-28 pb-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
          {/* Header */}
          <div className="text-center mb-10">
            <Camera className="w-10 h-10 text-amber-400 mx-auto mb-4" />
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2" data-testid="intake-title">Start Your AI Photoshoot</h1>
            <p className="text-gray-400">Upload your photo and tell us how you want to show up.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* FULL NAME & EMAIL */}
            <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-6 space-y-4">
              <h2 className="text-lg font-bold text-white mb-2">Client Details</h2>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Full Name <span className="text-amber-400">*</span></label>
                <Input value={form.fullName} onChange={(e) => handleInputChange('fullName', e.target.value)} placeholder="Your full name" required className="bg-black border-gray-700 text-white" data-testid="intake-fullName" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Email Address <span className="text-amber-400">*</span></label>
                <Input type="email" value={form.email} onChange={(e) => handleInputChange('email', e.target.value)} placeholder="your@email.com" required className="bg-black border-gray-700 text-white" data-testid="intake-email" />
              </div>
            </div>

            {/* PHOTO UPLOAD */}
            <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-6">
              <h2 className="text-lg font-bold text-white mb-1">Upload 1-2 Photos <span className="text-amber-400">*</span></h2>
              <p className="text-gray-500 text-sm mb-2">Clear, well-lit photos (JPG, PNG — Max 10MB each)</p>

              {photos.length < 2 && (
                <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-700 hover:border-amber-600/50 rounded-xl p-8 cursor-pointer transition-colors mb-4" data-testid="photo-upload-area">
                  <Upload className="w-10 h-10 text-gray-500 mb-3" />
                  <span className="text-gray-400 text-sm font-semibold">Click to upload or drag & drop</span>
                  <span className="text-gray-600 text-xs mt-1">JPG, PNG — Max 10MB</span>
                  <input type="file" accept="image/jpeg,image/png,image/jpg" onChange={handleFileChange} className="hidden" multiple />
                </label>
              )}

              {photos.length > 0 && (
                <div className="flex gap-4 flex-wrap">
                  {photos.map((p, i) => (
                    <div key={i} className="relative bg-gray-800 rounded-xl p-3 flex items-center gap-3">
                      <FileImage className="w-8 h-8 text-amber-400" />
                      <div>
                        <p className="text-white text-sm truncate max-w-[180px]">{p.name}</p>
                        <p className="text-gray-500 text-xs">{(p.size / 1024 / 1024).toFixed(1)} MB</p>
                      </div>
                      <button type="button" onClick={() => removePhoto(i)} className="absolute -top-2 -right-2 bg-red-600 rounded-full p-1">
                        <X className="w-3 h-3 text-white" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* PHOTOSHOOT PREFERENCES */}
            <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-6 space-y-4">
              <h2 className="text-lg font-bold text-white mb-2">Photoshoot Preferences</h2>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Desired Photoshoot Setting</label>
                <Input value={form.desiredSetting} onChange={(e) => handleInputChange('desiredSetting', e.target.value)} placeholder="e.g., CEO boardroom, city skyline, luxury lifestyle..." className="bg-black border-gray-700 text-white" data-testid="intake-setting" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Wardrobe Preferences</label>
                <Input value={form.wardrobePreferences} onChange={(e) => handleInputChange('wardrobePreferences', e.target.value)} placeholder="e.g., Navy suit, all white, bold colors..." className="bg-black border-gray-700 text-white" data-testid="intake-wardrobe" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Color Preferences</label>
                <Input value={form.colorPreferences} onChange={(e) => handleInputChange('colorPreferences', e.target.value)} placeholder="e.g., Earth tones, black & gold, neutral palette..." className="bg-black border-gray-700 text-white" data-testid="intake-colors" />
              </div>
            </div>

            {/* PHOTO USAGE */}
            <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-6 space-y-4">
              <h2 className="text-lg font-bold text-white mb-2">What will you be using these photos for? <span className="text-amber-400">*</span></h2>
              <select
                value={form.photoUsage}
                onChange={(e) => handleInputChange('photoUsage', e.target.value)}
                required
                className="w-full bg-black border border-gray-700 text-white rounded-lg px-4 py-3 text-sm focus:ring-amber-500 focus:border-amber-500 appearance-none"
                data-testid="intake-photoUsage"
              >
                <option value="" disabled>Select an option...</option>
                <option value="Social Media">Social Media</option>
                <option value="Business Branding">Business Branding</option>
                <option value="Website">Website</option>
                <option value="Speaking / Press">Speaking / Press</option>
                <option value="Dating Profile">Dating Profile</option>
                <option value="Personal Use">Personal Use</option>
                <option value="Event / Celebration">Event / Celebration</option>
                <option value="Other">Other</option>
              </select>
              {form.photoUsage === 'Other' && (
                <Input
                  value={form.photoUsageOther}
                  onChange={(e) => handleInputChange('photoUsageOther', e.target.value)}
                  placeholder="Please specify..."
                  required
                  className="bg-black border-gray-700 text-white"
                  data-testid="intake-photoUsageOther"
                />
              )}
            </div>

            {/* ADDITIONAL NOTES */}
            <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-6">
              <h2 className="text-lg font-bold text-white mb-2">Additional Notes</h2>
              <textarea value={form.additionalNotes} onChange={(e) => handleInputChange('additionalNotes', e.target.value)} placeholder="Any special requests, inspiration references, or details..." rows={4} className="w-full bg-black border border-gray-700 text-white rounded-lg px-4 py-3 text-sm focus:ring-amber-500 focus:border-amber-500" data-testid="intake-additionalNotes" />
            </div>

            {error && (
              <div className="flex items-center gap-2 bg-red-900/20 border border-red-800 text-red-400 rounded-xl p-4 text-sm">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                {error}
              </div>
            )}

            <Button type="submit" disabled={submitting} className="w-full bg-gradient-to-r from-amber-600 to-yellow-600 hover:from-amber-700 hover:to-yellow-700 text-black font-bold py-4 text-lg rounded-full" data-testid="intake-submit-btn">
              {submitting ? 'Submitting...' : 'Submit My Photoshoot'}
              {!submitting && <ArrowRight className="w-5 h-5 ml-2" />}
            </Button>

            <p className="text-center text-gray-600 text-xs">Turnaround time begins after submission (24-48 hours)</p>
          </form>

          {/* Footer branding */}
          <div className="mt-12 text-center">
            <p className="text-amber-400/80 text-sm font-semibold tracking-wider">eBlackBook AI Luxury Photoshoots</p>
            <p className="text-gray-600 text-xs mt-1">Powered by eBlackBookListings</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIPhotoshootsIntakePage;
